import { type ReactNode, useRef, useState } from "react";

import { CalendarDateTime, createCalendar } from "@internationalized/date";
import {
  type AriaDateRangePickerProps,
  type DateValue,
  useDateRangePicker,
  useLocale,
} from "react-aria";
import { Dialog } from "react-aria-components";
import { useDateFieldState, useDateRangePickerState } from "react-stately";

import { Button } from "#components/Button";
import { FormFieldAssistiveText } from "#components/shared/FormFieldAssistiveText";
import { FormFieldHeader } from "#components/shared/FormFieldHeader";
import { CalendarIcon, DashIcon } from "#icons";
import {
  cn,
  useTimezoneOptions,
  initializeYearOptions,
  isDateTimeType,
  rangeToZonedRange,
  customTv,
  type RangeValue,
  type MappedDateValue,
} from "#utils";

import { RangeCalendar } from "./RangeCalendar";
import { DateField } from "../DatePicker/DateField";
import { Popover } from "../DatePicker/Popover";

const triggerStyles = customTv({
  base: `
    flex items-center justify-between rounded-1 outline-none border border-outline-input-enabled
    hover:border-outline-input-hover hover:ring-1 hover:ring-outline-input-hover px-4 py-2 gap-x-4
  `,
  variants: {
    isInvalid: {
      true: "border-outline-negative ring-1 ring-outline-negative",
    },
    isFocused: {
      true: "ring-1 !ring-outline-input-focus !border-outline-input-focus",
    },
    isDisabled: {
      true: "cursor-not-allowed [&_*]:!cursor-not-allowed border-outline-input-disabled bg-background-primary hover:!ring-0 hover:!border-outline-input-disabled",
    },
    isReadOnly: {
      true: "!bg-background-secondary !border-transparent hover:!ring-0",
    },
  },
});

export interface DateRangePickerProps<T extends DateValue> extends AriaDateRangePickerProps<T> {
  onRangeChange: (range: { start: string; end: string }) => void;
  granularity?: "second" | "day";
  yearOptions?: number[];
  assistiveText?: ReactNode;
  tooltipText?: ReactNode;
  className?: string;
}
export function DateRangePicker<T extends DateValue>({
  yearOptions,
  granularity = "day",
  onRangeChange,
  tooltipText,
  className,
  ...props
}: DateRangePickerProps<T>) {
  const { years, minDate, maxDate } = initializeYearOptions(
    yearOptions,
    props.minValue,
    props.maxValue,
  );

  const { locale } = useLocale();

  const ref = useRef<HTMLDivElement>(null);

  const { userTimezone, timezoneOptions } = useTimezoneOptions();
  const [timezone, setTimezone] = useState(userTimezone);

  function onChange<T>(range: RangeValue<MappedDateValue<T>> | null) {
    if (!range) return;
    if (timezone && isDateTimeType(range.start) && isDateTimeType(range.end)) {
      try {
        onRangeChange(rangeToZonedRange(range.start, range.end, timezone, locale));
      } catch (e) {
        console.error(e);
      }
    } else {
      onRangeChange({
        start: range.start.toDate("UTC").toString(),
        end: range.end.toDate("UTC").toString(),
      });
    }
  }

  const state = useDateRangePickerState({
    ...props,
    shouldCloseOnSelect: false,
    granularity: granularity,
    shouldForceLeadingZeros: true,
    maxValue: maxDate,
    minValue: minDate,
    onChange,
  });

  const {
    labelProps,
    groupProps,
    startFieldProps,
    endFieldProps,
    buttonProps,
    dialogProps,
    calendarProps,
  } = useDateRangePicker(
    {
      ...props,
      granularity: granularity,
      shouldForceLeadingZeros: true,
      maxValue: maxDate,
      minValue: minDate,
      onChange,
    },
    state,
    ref,
  );

  const startFieldState = useDateFieldState({
    ...startFieldProps,
    locale,
    createCalendar,
  });
  const endFieldState = useDateFieldState({
    ...endFieldProps,
    locale,
    createCalendar,
  });

  const isInvalid = props.isInvalid || state.realtimeValidation.isInvalid;
  const isDisabled = props.isDisabled ?? false;
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div className={cn("flex flex-col gap-[.15rem]", className)}>
      <FormFieldHeader
        isDisabled={isDisabled}
        label={props.label}
        labelProps={labelProps}
        tooltipText={tooltipText}
      />
      <div
        className={triggerStyles({
          isFocused: !isDisabled && !props.isReadOnly && isFocused,
          isInvalid,
          isDisabled,
          isReadOnly: props.isReadOnly,
        })}
        {...groupProps}
        ref={ref}
      >
        <div className="flex flex-row items-center gap-x-4">
          <DateField
            defaultTimeOfDay="start"
            focusInputContainer={setIsFocused}
            {...startFieldProps}
          />
          <DashIcon
            className={cn("text-text-primary", isDisabled ? "text-text-disabled opacity-70" : "")}
            size={20}
          />
          <DateField defaultTimeOfDay="end" focusInputContainer={setIsFocused} {...endFieldProps} />
        </div>
        <Button
          className={cn(
            "p-0",
            isDisabled ? "text-text-disabled" : "",
            props.isReadOnly ? "!text-text-primary" : "",
          )}
          isDisabled={isDisabled || props.isReadOnly}
          onPress={() => state.open()}
          variant="plain"
          {...buttonProps}
        >
          <CalendarIcon className="size-lg" />
        </Button>
      </div>
      {props.assistiveText || state.realtimeValidation.validationErrors.length ? (
        <FormFieldAssistiveText isDisabled={isDisabled} isInvalid={isInvalid}>
          {state.realtimeValidation.validationErrors.length
            ? state.realtimeValidation.validationErrors.join(", ")
            : props.assistiveText}
        </FormFieldAssistiveText>
      ) : null}

      {state.isOpen ? (
        <Popover placement="bottom" state={state} triggerRef={ref}>
          <Dialog
            {...dialogProps}
            className="w-[22.5rem] rounded-md border border-outline-decorative bg-background-primary p-xl shadow-md"
          >
            <RangeCalendar
              {...calendarProps}
              dateFieldEndState={endFieldState}
              dateFieldStartState={startFieldState}
              dateRangePickerState={state}
              endFieldProps={endFieldProps}
              firstDayOfWeek={props.firstDayOfWeek}
              onChange={(dateRange) => {
                state.setDateRange(dateRange);

                // Clicking a date range in the calendar will set the time to 00:00:00 for the start time and 23:59:59 for the end time
                // This is based on convention that we select date first then more granular options
                if (granularity === "second") {
                  const newRange = {
                    start: new CalendarDateTime(
                      dateRange.start.year,
                      dateRange.start.month,
                      dateRange.start.day,
                      0,
                      0,
                      0,
                    ),
                    end: new CalendarDateTime(
                      dateRange.end.year,
                      dateRange.end.month,
                      dateRange.end.day,
                      23,
                      59,
                      59,
                    ),
                  };
                  state.setValue(newRange);
                }
              }}
              setTimezone={setTimezone}
              startFieldProps={startFieldProps}
              timezone={timezone}
              timezoneOptions={timezoneOptions}
              yearOptions={years}
            />
          </Dialog>
        </Popover>
      ) : null}
    </div>
  );
}
